import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useCallback, useEffect, useMemo, useState } from 'react';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import { OTOFLEX } from 'services/consts';

import AppLogger from 'utils/AppLogger';

/**
 * @author @omerrkosar
 */

const ViewTransactionHistory = NiceModal.create(({ id, fullName, balance }) => {
	const modal = useModal();
	const [isLoading, setIsLoading] = useState(false);
	const [transactions, setTransactions] = useState([]);
	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();
	const defaultColSpan = {
		md: 6
	};
	// If mode is edit or view we gotta get the user data .
	const fillForm = useCallback(() => {
		setIsLoading(true);
		axiosPrivate
			.post(OTOFLEX.GET_TRANSACTIONS, { userID: id })
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					data.data && setTransactions(data.data);
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [id]);

	// If the ID came from list page, get the user details
	useEffect(() => {
		if (id) fillForm();
	}, [id]);
	const { getLocaleDateWithTime } = useLocale();
	const { Currency } = useLocale();
	const columns = useMemo(
		() => [
			{
				header: 'date',
				accessorKey: 'date',
				size: 25,
				renderCell: ({ row }) => <Typography>{getLocaleDateWithTime(row.original.date)}</Typography>
			},
			{
				header: 'type',
				accessorKey: 'type',
				size: 25,
				renderCell: ({ row }) => <Typography>{row.original.type}</Typography>,
				showOnMobile: true
			},
			{
				header: 'otoOrderID',
				accessorKey: 'otoOrderID',
				size: 25,
				renderCell: ({ row }) => <Typography>{row.original.orderID}</Typography>
			},
			{
				header: 'amount',
				accessorKey: 'amount',
				size: 25,
				renderCell: ({ row }) => (
					<Typography withCurrency currency={row.original.currency}>
						{row.original.amount}
					</Typography>
				),
				showOnMobile: true
			}
		],
		[]
	);

	return (
		<Modal fullwidth maxWidth="small" className="modal-view-transaction-history">
			<ModalTitle>
				<Typography variant="p1">transactionDetails</Typography>
			</ModalTitle>
			<ModalContent>
				<Form>
					<FormItem colSpan={defaultColSpan} label="driverName" isLoading={isLoading}>
						<StringInput viewMode={true} value={fullName} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="balance" isLoading={isLoading}>
						<IntegerInput viewMode={true} value={balance ?? null} unit={Currency} />
					</FormItem>
				</Form>

				<div className="mt-3">
					{/* Mobile scroll fix */}
					<Table data={transactions} columns={columns} />
				</div>
			</ModalContent>

			<ModalActions>
				<Button label="close" loading={isLoading} variant="outline" onClick={() => modal.remove()} />
			</ModalActions>
		</Modal>
	);
});

export default ViewTransactionHistory;
